/**
 * The Benchmarks — Marketer View destination (SYR-68, Phase E).
 *
 * Two tabs that reuse the REAL existing pages (one source of truth, no drift):
 *  - Benchmarks → <BenchmarkingPage embedded> (the /dashboard/benchmarking
 *    content: exact selectors + subscription-gated chart types).
 *  - Trends → <TrendsTabs> (the /dashboard/analytics/trends Overview +
 *    Segment Comparison sub-tabs, gated by entitlements).
 * Brand comes from the global MarketerScope.
 */

import React, { useState } from 'react';
import { Tabs } from 'antd';
import { BarChartOutlined, LineChartOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '../../../hooks/useAuth';
import BenchmarkingPage from '../../../pages/BenchmarkingPage';
import { TrendsTabs } from '../../../pages/BrandTrendsHistoryPage';
import {
  useMarketerScope,
  periodTypeToDaysString,
} from '../scope/MarketerScopeContext';
import { MarketerPageHeader } from './MarketerPageHeader';
import { MarketerLoading } from '../atoms/MarketerLoading';
import { MarketerResponsesNeeded } from '../components/MarketerResponsesNeeded';
import { useScopedBrandEquity } from '../useScopedBrandEquity';

type Tab = 'benchmarks' | 'trends';

export const BenchmarksPage: React.FunctionComponent = () => {
  const intl = useIntl();
  const { user } = useAuth();
  const { activeBrand, scope } = useMarketerScope();
  const [tab, setTab] = useState<Tab>('benchmarks');
  const orgId = user?.organisation?.id?.toString();
  // Drive Benchmarks + Trends off the SAME global scope period as the rest of
  // the marketer view, so the Brand Equity number is identical across tabs
  // (SYR-68 data-consistency fix).
  const periodString = periodTypeToDaysString(scope.periodType);
  // Gate on data sufficiency so a new brand sees the on-brand "collect
  // responses" state instead of empty/barren benchmark + trend charts.
  const { response, isLoading: beLoading } = useScopedBrandEquity();

  if (!activeBrand || !orgId) {
    return <MarketerLoading />;
  }
  if (beLoading && !response) {
    return <MarketerLoading />;
  }

  // Not enough responses yet — show the contained "More Responses Needed" card
  // inside each tab (header + tab bar stay), matching the dashboard.
  const insufficient = !!response?.insufficientSampleSize;
  const emptyState = (
    <MarketerResponsesNeeded
      totalResponses={response?.totalResponses ?? 0}
      fromLabel={intl.formatMessage({
        id: 'marketerView.nav.benchmarks',
        defaultMessage: 'The Benchmarks',
      })}
      description={intl.formatMessage(
        {
          id: 'marketerView.benchmarks.insufficient',
          defaultMessage:
            'Benchmarks and trends need at least 10 survey responses. You currently have {count} response{plural}.',
        },
        {
          count: response?.totalResponses ?? 0,
          plural: (response?.totalResponses ?? 0) === 1 ? '' : 's',
        },
      )}
    />
  );

  return (
    <div>
      <MarketerPageHeader
        title={
          <FormattedMessage id="marketerView.nav.benchmarks" defaultMessage="The Benchmarks" />
        }
        subtitle={
          <FormattedMessage
            id="marketerView.benchmarks.sub"
            defaultMessage="How your brand stands against the market, and how it's moving over time."
          />
        }
      />
      <Tabs
        activeKey={tab}
        // Unmount the inactive tab — otherwise its charts stay mounted at
        // display:none (0 width) and Recharts logs negative-width <rect> errors.
        destroyInactiveTabPane
        onChange={(k) => {
          setTab(k as Tab);
          window.scrollTo({ top: 0 });
        }}
        items={[
          {
            key: 'benchmarks' as Tab,
            label: (
              <span>
                <BarChartOutlined style={{ marginRight: 8 }} />
                <FormattedMessage id="marketerView.benchmarks.tab.benchmarks" defaultMessage="Benchmarks" />
              </span>
            ),
            children: insufficient ? (
              emptyState
            ) : (
              <BenchmarkingPage
                embedded
                brandId={activeBrand.id}
                periodOverride={periodString}
              />
            ),
          },
          {
            key: 'trends' as Tab,
            label: (
              <span>
                <LineChartOutlined style={{ marginRight: 8 }} />
                <FormattedMessage id="marketerView.benchmarks.tab.trends" defaultMessage="Trends" />
              </span>
            ),
            children: insufficient ? (
              emptyState
            ) : (
              <TrendsTabs
                brandHash={activeBrand.hash}
                brandName={activeBrand.name}
                orgId={orgId}
                period={periodString}
              />
            ),
          },
        ]}
      />
    </div>
  );
};
